// src/components/Navbar.js
import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import ConfirmDialog from './ConfirmDialog';

const Navbar = ({ links, isMobile = false, showSubmenus = false }) => {
    // Get role from AuthContext to be able to hide items for certain roles
    const { userData } = useAuth() || {};
    const location = useLocation();
    const [expandedItems, setExpandedItems] = useState({});
    const [showRestrictedModal, setShowRestrictedModal] = useState(false);
    const [restrictedInfo, setRestrictedInfo] = useState({ title: '', message: '' });

    // Determinar si mostrar submenús (solo móvil, solo user y superadmin)
    const shouldShowSubmenus = isMobile && showSubmenus && userData && 
        (String(userData.rol || '').toLowerCase() === 'user' || 
         String(userData.rol || '').toLowerCase() === 'superadmin');

    // Si user is 'residente', hide the Administración link
    const visibleLinks = (links || []).filter((link) => {
        if (!userData) return true; // no user data yet, show links
        const isResidente = String(userData.rol || '').toLowerCase() === 'residente';
        if (isResidente && (link.to === '/administracion' || (link.label || '').toLowerCase().includes('administr'))) {
            return false;
        }
        return true;
    });

    // Detectar si algún submenú debe estar expandido por defecto (si la ruta actual coincide)
    useEffect(() => {
        if (shouldShowSubmenus) {
            const newExpanded = {};
            visibleLinks.forEach((link, index) => {
                if (link.submenu && link.submenu.length > 0) {
                    // Verificar si alguna ruta del submenú coincide con la ruta actual
                    const hasActiveSubmenu = link.submenu.some(subItem => 
                        location.pathname.startsWith(subItem.to)
                    );
                    if (hasActiveSubmenu || location.pathname === link.to) {
                        newExpanded[index] = true;
                    }
                }
            });
            setExpandedItems(newExpanded);
        }
    }, [location.pathname, shouldShowSubmenus]);

    const toggleSubmenu = (index, e) => {
        e.preventDefault();
        e.stopPropagation();
        setExpandedItems(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const handleRestrictedClick = (e, link) => {
        if (link.restricted) {
            e.preventDefault();
            setRestrictedInfo({
                title: 'Acceso Restringido',
                message: link.tooltip || 'Esta sección está bloqueada por morosidad. Ponte al corriente con tus pagos para acceder.'
            });
            setShowRestrictedModal(true);
        }
    };

    const handleMainLinkClick = (e, link, index) => {
        if (shouldShowSubmenus && link.submenu && link.submenu.length > 0) {
            // Si tiene submenú, expandir/colapsar en lugar de navegar
            toggleSubmenu(index, e);
        } else {
            // Si no tiene submenú o no estamos en modo submenús, comportamiento normal
            handleRestrictedClick(e, link);
        }
    };

    return (
        <nav className="main-nav">
            <ul className="nav-list">
                {visibleLinks.map((link, index) => {
                    const hasSubmenu = shouldShowSubmenus && link.submenu && link.submenu.length > 0;
                    const isExpanded = expandedItems[index];
                    // Verificar si el link principal está activo o algún submenú
                    const isActive = location.pathname === link.to || 
                        (hasSubmenu && link.submenu.some(subItem => {
                            const subItemPath = subItem.to.split('?')[0];
                            const subItemQuery = subItem.to.includes('?') ? subItem.to.split('?')[1] : '';
                            const pathMatches = location.pathname === subItemPath;
                            const queryMatches = !subItemQuery || location.search.includes(subItemQuery.split('&')[0].split('=')[0]);
                            return pathMatches && queryMatches;
                        }));

                    return (
                        <li 
                            className={`nav-item ${link.restricted ? 'nav-item-restricted' : ''} ${hasSubmenu ? 'nav-item-with-submenu' : ''} ${isExpanded ? 'nav-item-expanded' : ''}`}
                            key={index}
                            title={link.restricted ? link.tooltip : ''}
                        >
                            <NavLink 
                                to={link.to} 
                                className={({ isActive: navIsActive }) => 
                                    `nav-link ${navIsActive || isActive ? 'active' : ''} ${link.restricted ? 'nav-link-restricted' : ''} ${hasSubmenu ? 'nav-link-with-submenu' : ''}`
                                }
                                onClick={(e) => handleMainLinkClick(e, link, index)}
                                end={!hasSubmenu}
                            >
                                {link.icon}
                                <span>{link.label}</span>
                                {link.restricted && <span className="restricted-badge">🔒</span>}
                                {hasSubmenu && (
                                    <span className="submenu-toggle">
                                        {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                    </span>
                                )}
                            </NavLink>
                            {hasSubmenu && isExpanded && (
                                <ul className="nav-submenu">
                                    {link.submenu.map((subItem, subIndex) => {
                                        // Verificar si la ruta coincide (incluyendo query params)
                                        const subItemPath = subItem.to.split('?')[0];
                                        const subItemQuery = subItem.to.includes('?') ? subItem.to.split('?')[1] : '';
                                        const currentPath = location.pathname;
                                        const currentQuery = location.search;
                                        
                                        // Verificar si la ruta coincide
                                        const pathMatches = currentPath === subItemPath;
                                        // Si hay query params en el subItem, verificar que coincidan
                                        const queryMatches = !subItemQuery || currentQuery.includes(subItemQuery.split('&')[0].split('=')[0]);
                                        const isSubActive = pathMatches && queryMatches;
                                        
                                        // Verificar si este subItem tiene su propio submenú (submenús anidados)
                                        const hasNestedSubmenu = subItem.submenu && subItem.submenu.length > 0;
                                        const nestedSubmenuKey = `${index}-${subIndex}`;
                                        const isNestedExpanded = expandedItems[nestedSubmenuKey];
                                        
                                        return (
                                            <li key={subIndex} className={`nav-submenu-item ${hasNestedSubmenu ? 'nav-submenu-item-with-submenu' : ''}`}>
                                                {hasNestedSubmenu ? (
                                                    <>
                                                        <div 
                                                            className={`nav-submenu-link ${isSubActive ? 'active' : ''}`}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                toggleSubmenu(nestedSubmenuKey, e);
                                                            }}
                                                        >
                                                            {subItem.icon && <span className="submenu-icon">{subItem.icon}</span>}
                                                            <span>{subItem.label}</span>
                                                            {subItem.restricted && <span className="restricted-badge">🔒</span>}
                                                            <span className="submenu-toggle">
                                                                {isNestedExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                                            </span>
                                                        </div>
                                                        {isNestedExpanded && (
                                                            <ul className="nav-submenu-nested">
                                                                {subItem.submenu.map((nestedItem, nestedIndex) => {
                                                                    const nestedPath = nestedItem.to.split('?')[0];
                                                                    const nestedQuery = nestedItem.to.includes('?') ? nestedItem.to.split('?')[1] : '';
                                                                    const nestedPathMatches = location.pathname === nestedPath;
                                                                    const nestedQueryMatches = !nestedQuery || location.search.includes(nestedQuery.split('&')[0].split('=')[0]);
                                                                    const isNestedActive = nestedPathMatches && nestedQueryMatches;
                                                                    
                                                                    return (
                                                                        <li key={nestedIndex} className="nav-submenu-nested-item">
                                                                            <NavLink
                                                                                to={nestedItem.to}
                                                                                className={({ isActive: nestedIsActive }) => 
                                                                                    `nav-submenu-nested-link ${nestedIsActive || isNestedActive ? 'active' : ''}`
                                                                                }
                                                                                onClick={(e) => handleRestrictedClick(e, nestedItem)}
                                                                            >
                                                                                {nestedItem.icon && <span className="submenu-icon">{nestedItem.icon}</span>}
                                                                                <span>{nestedItem.label}</span>
                                                                                {nestedItem.restricted && <span className="restricted-badge">🔒</span>}
                                                                            </NavLink>
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        )}
                                                    </>
                                                ) : (
                                                    <NavLink
                                                        to={subItem.to}
                                                        className={({ isActive: subIsActive }) => 
                                                            `nav-submenu-link ${subIsActive || isSubActive ? 'active' : ''}`
                                                        }
                                                        onClick={(e) => handleRestrictedClick(e, subItem)}
                                                    >
                                                        {subItem.icon && <span className="submenu-icon">{subItem.icon}</span>}
                                                        <span>{subItem.label}</span>
                                                        {subItem.restricted && <span className="restricted-badge">🔒</span>}
                                                    </NavLink>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </li>
                    );
                })}
            </ul>

            <ConfirmDialog
                open={showRestrictedModal}
                title={restrictedInfo.title}
                message={restrictedInfo.message}
                confirmLabel="Entendido"
                showCancel={false}
                onConfirm={() => setShowRestrictedModal(false)}
                tone="warning"
                theme="premium"
            />
        </nav>
    );
};

export default Navbar;